import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../config';
import './DataPage.css';

function FinanceiroPixConfig() {
  const [config, setConfig] = useState({
    tipo_chave: 'cnpj',
    chave: '',
    titular_nome: '',
    cidade: '',
    instrucoes: '',
    ativo: false,
    usar_falavippay: false,
    falavippay_api_url: 'https://api.pay.viptecnologia.com.br',
    falavippay_api_key: '',
    mensagem_whatsapp: `Olá *{primeiro_nome}*! 👋

Para liberarmos o envio dos seus exames do protocolo *{protocolo}*, regularize o pagamento via PIX:

💠 *Chave PIX ({tipo_chave})*:
\`{chave_pix}\`

Beneficiário: *{titular}*
Valor: *R$ {valor}*

Após pagar, envie o comprovante por aqui que liberamos o envio do seu resultado automaticamente! 😊`,
  });
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [msg, setMsg] = useState(null);

  const [previewValor, setPreviewValor] = useState('50.00');
  const [previewTxid, setPreviewTxid] = useState('TESTE123');
  const [qrData, setQrData] = useState(null);
  const [brCode, setBrCode] = useState('');
  const [previewModo, setPreviewModo] = useState('estatico');
  const [previewTxidResult, setPreviewTxidResult] = useState(null);
  const [previewError, setPreviewError] = useState(null);

  const carregarConfig = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/financeiro/pix-config`);
      setConfig(prev => ({
        tipo_chave: res.data.tipo_chave || 'cnpj',
        chave: res.data.chave || '',
        titular_nome: res.data.titular_nome || '',
        cidade: res.data.cidade || '',
        instrucoes: res.data.instrucoes || '',
        ativo: res.data.ativo || false,
        usar_falavippay: res.data.usar_falavippay || false,
        falavippay_api_url: res.data.falavippay_api_url || 'https://api.pay.viptecnologia.com.br',
        falavippay_api_key: res.data.falavippay_api_key || '',
        mensagem_whatsapp: res.data.mensagem_whatsapp || prev.mensagem_whatsapp,
      }));
    } catch (err) {
      console.error(err);
      setMsg({ tipo: 'erro', texto: 'Erro ao carregar configuração' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { carregarConfig(); }, [carregarConfig]);

  const gerarPreview = useCallback(async () => {
    if (!config.chave || !config.titular_nome || !config.cidade) {
      setQrData(null);
      setBrCode('');
      setPreviewError('Preencha chave, titular e cidade para gerar o preview');
      return;
    }
    try {
      setPreviewError(null);
      const params = new URLSearchParams();
      if (previewValor) params.append('valor', previewValor);
      if (previewTxid) params.append('txid', previewTxid);
      const res = await axios.get(`${API_URL}/financeiro/pix-config/qr-preview?${params}`);
      setQrData(res.data.qrDataUrl);
      setBrCode(res.data.brCode);
      setPreviewModo(res.data.modo || 'estatico');
      setPreviewTxidResult(res.data.txid || null);
      if (res.data.aviso) setPreviewError(res.data.aviso);
    } catch (err) {
      setPreviewError(err.response?.data?.error || 'Erro ao gerar preview');
      setQrData(null);
      setBrCode('');
      setPreviewModo('estatico');
      setPreviewTxidResult(null);
    }
  }, [config.chave, config.titular_nome, config.cidade, previewValor, previewTxid]);

  useEffect(() => {
    const t = setTimeout(() => { gerarPreview(); }, 500);
    return () => clearTimeout(t);
  }, [gerarPreview]);

  const salvar = async () => {
    try {
      setSalvando(true);
      setMsg(null);
      // Não envia api_key mascarada pra não apagar no backend
      const payload = { ...config };
      if (typeof payload.falavippay_api_key === 'string' && payload.falavippay_api_key.startsWith('••••')) {
        delete payload.falavippay_api_key;
      }
      const res = await axios.put(`${API_URL}/financeiro/pix-config`, payload);
      setConfig(prev => ({
        ...prev,
        ...res.data,
        falavippay_api_key: res.data.falavippay_api_key || '',
      }));
      setMsg({ tipo: 'sucesso', texto: 'Configuração salva com sucesso!' });
      setTimeout(() => setMsg(null), 3000);
    } catch (err) {
      setMsg({ tipo: 'erro', texto: err.response?.data?.error || 'Erro ao salvar' });
    } finally {
      setSalvando(false);
    }
  };

  const copiarBrCode = () => {
    if (brCode) {
      navigator.clipboard.writeText(brCode);
      setMsg({ tipo: 'sucesso', texto: 'BR Code copiado!' });
      setTimeout(() => setMsg(null), 2000);
    }
  };

  if (loading) return <div className="data-page"><div className="loading">Carregando...</div></div>;

  return (
    <div className="data-page">
      <div className="page-header">
        <h1>💠 Configuração PIX</h1>
        <p className="page-subtitle">Chave PIX global do laboratório para cobranças por WhatsApp</p>
      </div>

      {msg && (
        <div style={{
          padding: '12px 16px', borderRadius: 6, marginBottom: 16,
          background: msg.tipo === 'erro' ? '#fee' : '#efe',
          color: msg.tipo === 'erro' ? '#c00' : '#060',
          border: `1px solid ${msg.tipo === 'erro' ? '#fcc' : '#cfc'}`,
        }}>{msg.texto}</div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 400px', gap: 24 }}>
        {/* Formulário */}
        <div style={{ background: '#fff', borderRadius: 8, padding: 24, border: '1px solid #e0e0e0' }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: 16 }}>Dados da Chave PIX</h3>

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Tipo de Chave</label>
            <select
              value={config.tipo_chave}
              onChange={e => setConfig({ ...config, tipo_chave: e.target.value })}
              style={inputStyle}
            >
              <option value="cpf">CPF</option>
              <option value="cnpj">CNPJ</option>
              <option value="email">E-mail</option>
              <option value="telefone">Telefone</option>
              <option value="aleatoria">Aleatória</option>
            </select>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Chave PIX *</label>
            <input
              type="text"
              value={config.chave}
              onChange={e => setConfig({ ...config, chave: e.target.value })}
              placeholder={
                config.tipo_chave === 'cpf' ? '000.000.000-00' :
                config.tipo_chave === 'cnpj' ? '00.000.000/0000-00' :
                config.tipo_chave === 'email' ? 'contato@laboratorio.com.br' :
                config.tipo_chave === 'telefone' ? '+55 37 99999-9999' :
                'chave aleatória UUID'
              }
              style={inputStyle}
            />
            <small style={smallStyle}>Pontuação é removida automaticamente para CPF/CNPJ</small>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Nome do Titular (Beneficiário) *</label>
            <input
              type="text"
              value={config.titular_nome}
              onChange={e => setConfig({ ...config, titular_nome: e.target.value })}
              placeholder="LABORATORIO SANTA RITA LTDA"
              maxLength={25}
              style={inputStyle}
            />
            <small style={smallStyle}>Máximo 25 caracteres (padrão EMV) — sem acentos</small>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Cidade *</label>
            <input
              type="text"
              value={config.cidade}
              onChange={e => setConfig({ ...config, cidade: e.target.value })}
              placeholder="DIVINOPOLIS"
              maxLength={15}
              style={inputStyle}
            />
            <small style={smallStyle}>Máximo 15 caracteres — sem acentos</small>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Instruções ao paciente (opcional)</label>
            <textarea
              value={config.instrucoes}
              onChange={e => setConfig({ ...config, instrucoes: e.target.value })}
              placeholder="Após pagar, envie o comprovante aqui para liberarmos seus resultados."
              style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }}
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={config.ativo}
                onChange={e => setConfig({ ...config, ativo: e.target.checked })}
                style={{ width: 18, height: 18 }}
              />
              <strong>Ativar geração de PIX</strong>
            </label>
            <small style={smallStyle}>
              Quando ativo, o worker de pagamento pendente poderá anexar QR code ao paciente.
            </small>
          </div>

          <hr style={{ border: 'none', borderTop: '1px solid #eee', margin: '20px 0' }} />

          <h3 style={{ margin: '0 0 8px 0', fontSize: 15 }}>Mensagem de cobrança (WhatsApp)</h3>
          <p style={{ fontSize: 12, color: '#666', margin: '0 0 12px 0' }}>
            Enviada automaticamente quando o paciente clicar em <strong>OK</strong> no template de pendência financeira.
            Use as variáveis abaixo; elas são substituídas no momento do envio.
          </p>

          <div style={{ marginBottom: 16 }}>
            <textarea
              value={config.mensagem_whatsapp}
              onChange={e => setConfig({ ...config, mensagem_whatsapp: e.target.value })}
              style={{ ...inputStyle, minHeight: 180, resize: 'vertical', fontFamily: 'inherit', fontSize: 13 }}
            />
            <div style={{ marginTop: 8, padding: 8, background: '#f5f5f5', borderRadius: 4, fontSize: 11, color: '#555', lineHeight: 1.6 }}>
              <strong>Variáveis disponíveis:</strong><br />
              <code>{'{primeiro_nome}'}</code> · <code>{'{nome_paciente}'}</code> · <code>{'{protocolo}'}</code> ·{' '}
              <code>{'{chave_pix}'}</code> · <code>{'{tipo_chave}'}</code> · <code>{'{titular}'}</code> ·{' '}
              <code>{'{cidade}'}</code> · <code>{'{valor}'}</code> · <code>{'{instrucoes}'}</code>
            </div>
          </div>

          <hr style={{ border: 'none', borderTop: '1px solid #eee', margin: '20px 0' }} />

          <h3 style={{ margin: '0 0 8px 0', fontSize: 15 }}>Integração Sicoob (falavippay) <span style={{ fontSize: 11, color: '#888', fontWeight: 'normal' }}>— opcional</span></h3>
          <p style={{ fontSize: 12, color: '#666', margin: '0 0 16px 0' }}>
            Se informada, o copia-e-cola é gerado como <strong>PIX dinâmico real</strong> no banco (com TXID consultável e webhook de confirmação). Sem ela, usa PIX estático local.
          </p>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={config.usar_falavippay}
                onChange={e => setConfig({ ...config, usar_falavippay: e.target.checked })}
                style={{ width: 18, height: 18 }}
              />
              <strong>Usar falavippay para gerar cobranças</strong>
            </label>
          </div>

          {config.usar_falavippay && (
            <>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>URL da API falavippay</label>
                <input
                  type="text"
                  value={config.falavippay_api_url}
                  onChange={e => setConfig({ ...config, falavippay_api_url: e.target.value })}
                  placeholder="https://api.pay.viptecnologia.com.br"
                  style={inputStyle}
                />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>API Key (X-API-Key)</label>
                <input
                  type="text"
                  value={config.falavippay_api_key}
                  onChange={e => setConfig({ ...config, falavippay_api_key: e.target.value })}
                  placeholder="sk-xxx..."
                  style={{ ...inputStyle, fontFamily: 'monospace', fontSize: 13 }}
                />
                <small style={smallStyle}>
                  Cadastre um app em pay.viptecnologia.com.br e cole aqui a API key. Mascarada após salvar.
                </small>
              </div>
            </>
          )}

          <button
            onClick={salvar}
            disabled={salvando}
            style={{
              background: '#1976d2', color: '#fff', border: 'none',
              padding: '10px 24px', borderRadius: 6, cursor: salvando ? 'wait' : 'pointer',
              fontSize: 14, fontWeight: 600,
            }}
          >
            {salvando ? 'Salvando...' : 'Salvar configuração'}
          </button>
        </div>

        {/* Preview */}
        <div style={{ background: '#fafafa', borderRadius: 8, padding: 24, border: '1px solid #e0e0e0', height: 'fit-content' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <h3 style={{ margin: 0, fontSize: 16 }}>Preview QR Code</h3>
            {qrData && (
              <span style={{
                fontSize: 11, fontWeight: 600,
                padding: '3px 10px', borderRadius: 12,
                background: previewModo === 'dinamico' ? '#e8f5e9' : '#f5f5f5',
                color: previewModo === 'dinamico' ? '#2e7d32' : '#666',
                border: `1px solid ${previewModo === 'dinamico' ? '#81c784' : '#ddd'}`,
              }}>
                {previewModo === 'dinamico' ? '⚡ DINÂMICO (Sicoob)' : 'ESTÁTICO'}
              </span>
            )}
          </div>

          {previewTxidResult && (
            <div style={{ fontSize: 11, color: '#666', marginBottom: 12, fontFamily: 'monospace' }}>
              TXID: {previewTxidResult}
            </div>
          )}

          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Valor (simulação)</label>
            <input
              type="number"
              step="0.01"
              value={previewValor}
              onChange={e => setPreviewValor(e.target.value)}
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>TXID (simulação)</label>
            <input
              type="text"
              value={previewTxid}
              onChange={e => setPreviewTxid(e.target.value)}
              maxLength={25}
              style={inputStyle}
            />
          </div>

          {previewError ? (
            <div style={{ padding: 16, background: '#fff3e0', borderRadius: 6, color: '#e65100', fontSize: 13 }}>
              {previewError}
            </div>
          ) : qrData ? (
            <>
              <div style={{ textAlign: 'center', background: '#fff', padding: 16, borderRadius: 6 }}>
                <img src={qrData} alt="QR Code PIX" style={{ maxWidth: '100%' }} />
              </div>
              <div style={{ marginTop: 12 }}>
                <label style={labelStyle}>BR Code (Copia e Cola)</label>
                <textarea
                  value={brCode}
                  readOnly
                  style={{ ...inputStyle, fontSize: 11, fontFamily: 'monospace', minHeight: 80 }}
                />
                <button
                  onClick={copiarBrCode}
                  style={{
                    marginTop: 8, background: '#fff', border: '1px solid #1976d2',
                    color: '#1976d2', padding: '6px 12px', borderRadius: 4,
                    cursor: 'pointer', fontSize: 13,
                  }}
                >
                  📋 Copiar BR Code
                </button>
              </div>
            </>
          ) : (
            <div style={{ padding: 16, textAlign: 'center', color: '#888' }}>
              Preencha os dados para ver o preview
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const labelStyle = {
  display: 'block', marginBottom: 4, fontSize: 13, fontWeight: 500, color: '#333',
};

const inputStyle = {
  width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: 4,
  fontSize: 14, boxSizing: 'border-box',
};

const smallStyle = {
  display: 'block', marginTop: 4, fontSize: 12, color: '#888',
};

export default FinanceiroPixConfig;
